import './DodLayoutEditor.scss';
import React, { useMemo, useEffect, ReactNode, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import classnames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import Tippy from '@tippyjs/react';
import { format as formatDate } from 'date-fns';
import { ByzzerButton, ByzzerSelect, ByzzerSelectOptionGroup } from '@byzzer/ui-components';
import DodLayoutOptions from '@/components/DodConfigEditor/builders/DodLayoutBuilder/DodLayoutOptions/DodLayoutOptions';
import DodLayoutConditionBuilder from '@/components/DodConfigEditor/builders/DodLayoutBuilder/DodLayoutConditionBuilder/DodLayoutConditionBuilder';
import DodDesignYourLayout from '@/components/DodConfigEditor/builders/DodLayoutBuilder/DodDesignYourLayout/DodDesignYourLayout';
import { DodPanel } from '@/components/DodConfigEditor/common/DodPanel';
import { DodPresetConfig, RowColConfig } from '@/components/DodConfigEditor/types';
import { useDodWizard } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import { openDodConditionBuilderModal } from '@/components/DodConfigEditor/builders/DodLayoutBuilder/DodLayoutConditionBuilder/DodConditionBuilderModal';
import { toAxisDef } from '@/components/DodConfigEditor/toAxisDef';
import { confirm, openModal } from '@/components/form';
import {
    QUICK_LAYOUT_OPTIONS,
    quickLayoutAllowedOptions,
} from '@/components/DodConfigEditor/builders/DodLayoutBuilder/DodLayoutEditor/quickLayouts';
import { useApp, useUser } from '@/contexts/UserContext';
import { useUndo } from '@/hooks/useUndo';
import { useEventDataWithUserInfo, useTrackEvent } from '@/analytics/AnalyticsContext';
import { TimePeriodRange } from '@/utils/timePeriod/TimePeriodRange';
import {
    DodAxisDef,
    DodColDef,
    DodConditions,
    DodLayoutConfig,
    DodLayoutPreset,
    DodRowDef,
    DodRunConfig,
    SortType,
} from '@/types/DodRun';
import { openCreateDodPresetModal } from '@/components/DodConfigEditor/common/DodSavedSelection/DodSaveSelectionModal';
import { DodPresetType } from '@/types/ApiTypes';
import { useDodService } from '@/services/dodPresets.service';
import { factSetToDisplayNames, getConditionNamePreservingCustom } from '@/components/DodConfigEditor/common/utils';
import { difference, intersection, uniq, isEqual } from 'lodash';
import { alert as byzzerAlert } from '@/components/form';
import { SUB_DIMENSION_VALUE_TO_DISPLAY } from '@/components/DodConfigEditor/builders/DodLayoutBuilder/DodExcelLayoutPreview/DodExcelLayoutPreview';
import { caseInsensitiveReverseSort, caseInsensitiveSort } from '@/utils/Sort';
import { useTenantApi } from '@/hooks/useTenantApi';
import { DEFAULT_RUN_CONFIG } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardProvider';
import { ByzzerMask } from '@/components/ByzzerMask/ByzzerMask';

const baseClassName = 'dod-layout-editor';

export type DodLayoutEditorProps = {
    className?: string;
    name?: string;
    value: DodRunConfig;
    title: string;
    savedSelectionType: DodPresetType;
    savedSelectionDescription: ReactNode;
    isCreateRunMode?: boolean;
} & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

export type UndoState = {
    timePeriodsReordered?: boolean;
    conditions?: DodConditions;
    layout?: DodLayoutConfig;
    rowColConfig?: RowColConfig[];
};

type OrderConfig = {
    products: string[];
    markets: string[];
    time_periods: string[];
    facts: string[];
};

export function DodLayoutEditor({
    className,
    name,
    value,
    title,
    savedSelectionType,
    savedSelectionDescription,
    isCreateRunMode,
}: DodLayoutEditorProps) {
    const [searchParams] = useSearchParams();
    const { maxDataDates, reloadDodPresets } = useApp();
    const trackEvent = useTrackEvent();
    const getEventData = useEventDataWithUserInfo();
    const { addStateToHistory, undo, canUndo } = useUndo<UndoState>();
    const [saveSelectionModalOpened, setSaveSelectionModalOpened] = useState<boolean>(false);
    const [savingDefaultLayout, setSavingDefaultLayout] = useState<boolean>(false);

    const maxDate = useMemo<string>(() => {
        return formatDate(maxDataDates.rms!, 'yyyyMMdd');
    }, [maxDataDates]);

    const {
        runConfig,
        setRunConfig,
        setRowColConfigs,
        applyValues,
        rowColConfigs,
        suppressQuickLayoutWarning,
        setSuppressQuickLayoutWarning,
        timePeriodsReordered,
        setTimePeriodsReordered,
        presets,
        setPreset,
    } = useDodWizard();

    const { saveDefaultDodLayout, deleteDefaultDodLayout } = useTenantApi();

    const { dodExcelTemplates, user, refreshAccount } = useUser();
    const { dodPresetsMap, deletePreset } = useDodService();
    const [hoveredItem, setHoveredItem] = useState('');
    let defaultSelection = undefined;

    function hasSelectedConditions(conditions: DodConditions): boolean {
        // Conditions belong to the Layout step, but we only want to stamp step 5 when the user has
        // actually selected a condition. Clearing back to the defaults should not force the config
        // into step 5, because that would make unrelated follow-up API calls look like layout work.
        return (
            (conditions?.facts?.length ?? 0) > 0 ||
            conditions?.market !== 'each_market' ||
            conditions?.timePeriod !== 'each_time_period'
        );
    }

    const handleMouseEnter = (val) => setHoveredItem(val.toString());

    const mode = useMemo<string | null>(() => {
        return searchParams.get('mode');
    }, [searchParams]);

    const disabledQuickLayoutOptions = useMemo(() => {
        if (value.layout.includeSubTotals && mode === 'layout' && value.layout.quickLayoutCode) {
            return QUICK_LAYOUT_OPTIONS.filter(
                (ql) =>
                    value.layout.quickLayoutCode &&
                    !quickLayoutAllowedOptions[value.layout.quickLayoutCode].includes(ql.value)
            );
        }
        return [];
    }, []);

    const isDefaultLayout = (itemVal) => {
        return user?.metadata?.defaultDodLayoutId === itemVal.toString();
    };

    const layoutOptions = useMemo<ByzzerSelectOptionGroup[]>(() => {
        const excelLayoutTemplates = dodExcelTemplates.map((template) => ({
            display: <>Excel Template: {template.displayName}</>,
            value: `${template.id}`,
            data: toDodPresetConfig(template.layout),
        }));
        const savedLayouts = dodPresetsMap.layout.map((layoutPreset) => ({
            display: <>Saved Layout: {layoutPreset.displayName}</>,
            value: `${layoutPreset.id}`,
            data: {
                layout: {
                    ...toDodPresetConfig((layoutPreset.values as DodLayoutPreset).layout),
                    includeCategoryTotals: (layoutPreset.values as DodLayoutPreset).layout.includeCategoryTotals,
                    includeSubTotals: (layoutPreset.values as DodLayoutPreset).layout.includeSubTotals,
                    includeExtractDate: (layoutPreset.values as DodLayoutPreset).layout.includeExtractDate,
                    includeNoHistoryLabels: (layoutPreset.values as DodLayoutPreset).layout.includeNoHistoryLabels ?? false,
                    includeGranularityLevel: (layoutPreset.values as DodLayoutPreset).layout.includeGranularityLevel ?? false,
                    filterNotApplicable: (layoutPreset.values as DodLayoutPreset).layout.filterNotApplicable ?? false,
                },
                conditions: (layoutPreset.values as DodLayoutPreset).conditions,
            },
        }));

        // TODO: add ReactNode | string for the display in ByzzerSelectOptionGroup
        return [
            {
                label: 'Quick Layout',
                options: QUICK_LAYOUT_OPTIONS.map((ql) => {
                    if (disabledQuickLayoutOptions.find((dql) => dql.value === ql.value)) {
                        return {
                            ...ql,
                            display: (
                                <Tippy
                                    content={
                                        'This layout change requires your subtotals to be recalculated. Please modify your run instead of using the apply new layout option.'
                                    }
                                    placement={'bottom'}
                                    delay={[500, 0]}
                                >
                                    <span>{ql.display}</span>
                                </Tippy>
                            ),
                        };
                    }
                    return { ...ql };
                }),
            },
            { label: 'Saved Layouts', options: savedLayouts },
            { label: 'Excel Templates', options: excelLayoutTemplates },
        ];
    }, [dodExcelTemplates, dodPresetsMap]);

    const layoutId = useMemo<string>(() => {
        if (value.layout.templateId) {
            return `${value.layout.templateId}`;
        } else if (value.layout.savedLayoutId) {
            return `${value.layout.savedLayoutId}`;
        } else if (value.layout.quickLayoutCode) {
            return value.layout.quickLayoutCode;
        }
        return 'cl';
    }, [value.layout]);

    // the first time user lands on layout tab, layout's value will have only the default value
    // note: every property in layout object is necessary, hence entire object comparison
    const firstTimeLandedOnLayout = () => {
        // `extractWizardStep` and `wizardState` are transient workflow flags; they should not
        // influence whether the underlying layout config is still the default.
        const { extractWizardStep, wizardState, ...layoutForComparison }: {
            extractWizardStep?: number;
            wizardState?: number;
        } & Omit<DodLayoutConfig, 'extractWizardStep' | 'wizardState'> = value.layout;
        
        return isEqual(layoutForComparison, DEFAULT_RUN_CONFIG.layout);
    };

    // reset conditions everytime fact changes
    useEffect(() => {
        const formattedFactNames = value.facts.map((fact) => factSetToDisplayNames(fact)).flat(Infinity);
        // Support legacy FactCondition[] shape as well as the new DodConditions object shape
        const conditionFacts = Array.isArray(value.conditions)
            ? value.conditions
            : (value.conditions?.facts ?? []);
        const filteredFacts = conditionFacts.filter((condition) =>
            formattedFactNames.some((fact) => condition.fact === fact)
        );
        applyValues((prevValue) => {
            const nextConditions = {
                ...(Array.isArray(prevValue.runConfig.conditions) ? {} : prevValue.runConfig.conditions),
                facts: filteredFacts,
                market: Array.isArray(prevValue.runConfig.conditions) ? 'each_market' : (prevValue.runConfig.conditions?.market ?? 'each_market'),
                timePeriod: Array.isArray(prevValue.runConfig.conditions) ? 'each_time_period' : (prevValue.runConfig.conditions?.timePeriod ?? 'each_time_period'),
            };
            const nextRunConfig = {
                ...prevValue.runConfig,
                conditions: nextConditions,
            };

            return {
                ...prevValue,
                timePeriodsReordered: false,
                runConfig: {
                    ...nextRunConfig,
                    conditions: {
                        ...nextConditions,
                        name: getConditionNamePreservingCustom(prevValue.runConfig, nextRunConfig, maxDate),
                    },
                },
            };
        });
    }, [value.facts]);

    useEffect(() => {
        const currentLayoutOptions: string[] = layoutOptions
            .map((optionGroup) => optionGroup.options.map((option) => option.value.toString()))
            .flat();
        // if default layout is set and it is create run mode, then the default layout gets priority
        // we don't wanna override the layout selections for any other scenarios, except below condition
        if (
            user?.metadata?.defaultDodLayoutId && // default layout is set
            isCreateRunMode && // user in create new run mode
            currentLayoutOptions.includes(user?.metadata?.defaultDodLayoutId) && // default layout is a valid option
            firstTimeLandedOnLayout() // only the first paint of layout tab
        ) {
            // get the value or data for the default layout's id, this is the source of layout config
            const currentLayoutValue = layoutOptions
                .map((optionGroup) => optionGroup.options)
                .flat()
                .filter((option) => option.value === user?.metadata?.defaultDodLayoutId)[0].data;
            if (user?.metadata?.defaultDodLayoutId === 'cl') {
                // custom layout doesn't impact the layout in anyway
                setRunConfig((config) => {
                    handleUndoStates({ layout: config.layout });
                    return {
                        ...config,
                        layout: {
                            ...value.layout,
                            quickLayoutCode: 'cl',
                            templateId: undefined,
                            savedLayoutId: undefined,
                        },
                    };
                });
            } else {
                const isQuickLayout = user?.metadata?.defaultDodLayoutId.startsWith('ql');
                const wasQuickLayout = layoutId?.startsWith('ql');
                const quickLayoutCode = isQuickLayout ? user?.metadata?.defaultDodLayoutId : undefined;
                const isSavedLayout = Object.keys(currentLayoutValue).includes('conditions');
                const savedLayoutId = isQuickLayout
                    ? undefined
                    : isSavedLayout
                    ? Number(user?.metadata?.defaultDodLayoutId)
                    : undefined;
                const templateId =
                    isQuickLayout || isSavedLayout ? undefined : Number(user?.metadata?.defaultDodLayoutId);

                let includeCategoryTotals = value.layout.includeCategoryTotals;
                let includeSubTotals = value.layout.includeSubTotals;
                let includeExtractDate = value.layout.includeExtractDate;
                let includeNoHistoryLabels = value.layout.includeNoHistoryLabels;
                let includeGranularityLevel = value.layout.includeGranularityLevel;
                let filterNotApplicable = value.layout.filterNotApplicable;

                let conditions = value.conditions;

                let newRowColConfigs: RowColConfig[] = [];

                // need to handle saved layout different from quick layouts and excel templates
                // (mainly due to additional properties saved in a layout through this mode, which other modes are not aware of)
                if (isSavedLayout) {
                    if (mode !== 'layout') {
                        const savedConditions = currentLayoutValue?.conditions;
                        const savedConditionsObject =
                            savedConditions && !Array.isArray(savedConditions) ? savedConditions : undefined;
                        const savedFacts = Array.isArray(savedConditions)
                            ? savedConditions
                            : (savedConditionsObject?.facts ?? []);
                        const formattedFactNames = value.facts
                            .map((fact) => factSetToDisplayNames(fact))
                            .flat(Infinity);
                        const baseConditions = Array.isArray(value.conditions) ? {} : value.conditions;
                        const fallbackMarket =
                            Array.isArray(value.conditions) ? 'each_market' : (value.conditions?.market ?? 'each_market');
                        const fallbackTimePeriod =
                            Array.isArray(value.conditions)
                                ? 'each_time_period'
                                : (value.conditions?.timePeriod ?? 'each_time_period');

                        conditions = {
                            ...baseConditions,
                            ...(savedConditionsObject ?? {}),
                            facts: savedFacts.filter((condition) =>
                                formattedFactNames.some((fact) => condition.fact === fact)
                            ),
                            market: savedConditionsObject?.market ?? fallbackMarket,
                            timePeriod: savedConditionsObject?.timePeriod ?? fallbackTimePeriod,
                        };

                        includeCategoryTotals = currentLayoutValue.layout.includeCategoryTotals;
                        includeSubTotals = currentLayoutValue.layout.includeSubTotals;
                        includeExtractDate = currentLayoutValue.layout.includeExtractDate;
                        includeNoHistoryLabels = currentLayoutValue.layout.includeNoHistoryLabels ?? false;
                        includeGranularityLevel = currentLayoutValue.layout.includeGranularityLevel ?? false;
                        filterNotApplicable = currentLayoutValue.layout.filterNotApplicable ?? false;
                    }

                    const {
                        presetOrderConfigs,
                        currentOrderConfigs,
                        presetRowsAndColumns,
                    } = getPresetLayoutOrderAndCurrentLayoutOrder(Number(user?.metadata?.defaultDodLayoutId));

                    // find what values are common between preset's order and current selection order and put it in a json map
                    const intersectionOrderConfig = {
                        products: intersection(presetOrderConfigs['products'], currentOrderConfigs['products']),
                        markets: intersection(presetOrderConfigs['markets'], currentOrderConfigs['markets']),
                        time_periods: intersection(
                            presetOrderConfigs['time_periods'],
                            currentOrderConfigs['time_periods']
                        ),
                        facts: intersection(presetOrderConfigs['facts'], currentOrderConfigs['facts']),
                    };

                    // find what values present in current selection, but not in presets
                    // we want this because thats what the user has selected to run this dod
                    // we dont care of the values present in preset and not in current selection,
                    // simply because user did not select them for this run
                    const differenceOrderConfig = {
                        products: difference(currentOrderConfigs['products'], presetOrderConfigs['products']),
                        markets: difference(currentOrderConfigs['markets'], presetOrderConfigs['markets']),
                        time_periods: difference(
                            currentOrderConfigs['time_periods'],
                            presetOrderConfigs['time_periods']
                        ),
                        facts: difference(currentOrderConfigs['facts'], presetOrderConfigs['facts']),
                    };

                    const marketSort = presetRowsAndColumns.find((rcc) => rcc.type === 'markets')?.sortType;
                    const timePeriodSort = presetRowsAndColumns.find((rcc) => rcc.type === 'time_periods')?.sortType;

                    // as per requirement discussed for ordering in BYZ-8480, when a saved layout is applied in layout step,
                    // we place the common values first and then append the remaining values from current selection,
                    // which is happening below for all dimensions
                    const resultOrderConfig = {
                        products: [...intersectionOrderConfig['products'], ...differenceOrderConfig['products']],
                        markets:
                            marketSort === 'asc' || marketSort === 'desc'
                                ? getSortedMarkets(marketSort)
                                : [...intersectionOrderConfig['markets'], ...differenceOrderConfig['markets']],
                        time_periods:
                            timePeriodSort === 'asc' || timePeriodSort === 'desc'
                                ? getSortedTimePeriods(timePeriodSort)
                                : [
                                      ...intersectionOrderConfig['time_periods'],
                                      ...differenceOrderConfig['time_periods'],
                                  ],
                        facts: [...intersectionOrderConfig['facts'], ...differenceOrderConfig['facts']],
                    };

                    const presetRowColConfigs = applyPresetLayout(currentLayoutValue.layout);

                    // for markets, timeperiods and facts, its pretty straight-forward, we just need to update the 'values',
                    // other config like axis, stack, page, type, dim all remains the same
                    const nonProducts = presetRowColConfigs
                        .filter((config) => config.type !== 'products')
                        .map((config) => ({
                            ...config,
                            values: resultOrderConfig[config.type],
                        }));

                    // however for products, we search if the dim already exists in previous rowColConfig,
                    // if present, just return that object and not modify anything, as we want to preserve
                    // its config like page, stack, hide which user might have selected
                    const products: RowColConfig[] = resultOrderConfig['products'].map((dim) => {
                        if (presetRowColConfigs.find((config) => config.dim === dim)) {
                            if (dim === 'categories' && hasPartiallyApprovedMarkets) {
                                return {
                                    ...(presetRowColConfigs.find((config) => config.dim === dim) as RowColConfig),
                                    hide: false,
                                    stack: false,
                                };
                            }
                            return presetRowColConfigs.find((config) => config.dim === dim) as RowColConfig;
                        } else {
                            // if the dim isn't found in previous value of rowColConfig, then its time to create one object for it
                            return {
                                // we want to preserve the axis as any other product dim, rest is all default values
                                axis: presetRowColConfigs.filter((config) => config.type === 'products')[0].axis,
                                type: 'products',
                                dim: dim,
                                values: [],
                                sortType: 'default',
                                stack: false,
                                hide: false,
                                pageBy: false,
                            } as RowColConfig;
                        }
                    });

                    // finally, combine both product's rowColConfig and non-product's rowColConfig and return the value per overallUiOrder
                    const combinedRowColConfig = [...nonProducts, ...products];
                    const overallOrderForPreset = uniq(presetRowColConfigs.map((prcc) => prcc.type));
                    newRowColConfigs = overallOrderForPreset // to get the overall order to be applied from selected saved preset
                        .map((type) => combinedRowColConfig.filter((rcc) => rcc.type === type))
                        .flat();

                    // we need this to throw a warning popup to user
                    const excludedPresetSelections = {
                        products: difference(presetOrderConfigs['products'], currentOrderConfigs['products']),
                        markets: difference(presetOrderConfigs['markets'], currentOrderConfigs['markets']),
                        time_periods: difference(
                            presetOrderConfigs['time_periods'],
                            currentOrderConfigs['time_periods']
                        ),
                        facts: difference(presetOrderConfigs['facts'], currentOrderConfigs['facts']),
                    };
                } else {
                    newRowColConfigs = applyPresetLayout(currentLayoutValue);
                }
                const layout: DodLayoutConfig = {
                    ...runConfig.layout,
                    quickLayoutCode,
                    templateId,
                    savedLayoutId,
                    includeCategoryTotals,
                    includeSubTotals,
                    includeExtractDate,
                    includeNoHistoryLabels,
                    includeGranularityLevel,
                    filterNotApplicable,
                    overallUiOrder: uniq(newRowColConfigs.map((config) => config.type)),
                    rows: newRowColConfigs.filter((v) => v.axis === 'row').map(toAxisDef) as DodRowDef[],
                    columns: newRowColConfigs.filter((v) => v.axis === 'col').map(toAxisDef) as DodColDef[],
                };
                // set the base config based on the quick layout
                applyValues((prevValue) => {
                    handleUndoStates({
                        timePeriodsReordered: prevValue.timePeriodsReordered,
                        rowColConfig: prevValue.rowColConfigs,
                        layout: prevValue.runConfig.layout,
                        conditions: prevValue.runConfig.conditions,
                    });
                    return {
                        ...prevValue,
                        timePeriodsReordered: false,
                        rowColConfigs: newRowColConfigs,
                        runConfig: {
                            ...prevValue.runConfig,
                            layout,
                            conditions,
                        },
                    };
                });
            }
        }
    }, [user?.metadata?.defaultDodLayoutId]);

    //BYZ-12113 - Hide and stack option should be disabled always if any of the market in run is partially-approved
    const hasPartiallyApprovedMarkets = useMemo(
        () => runConfig.filters.markets.values.some((item) => item.marketHasPartialApproval === true),
        [runConfig.filters.markets.values]
    );

    function getSavedLayoutById(layoutId: number) {
        const savedLayout = dodPresetsMap.layout.find((preset) => Number(preset.id) === Number(layoutId));
        return savedLayout;
    }

    function handleLayoutChange(e: ByzzerChangeEvent<DodLayoutConfig>): void {
        setRunConfig((config) => {
            handleUndoStates({ layout: config.layout });
            return {
                ...config,
                layout: {
                    ...e.value,
                },
            };
        });
    }

    function getPresetLayoutOrderAndCurrentLayoutOrder(
        savedLayoutId: number
    ): { presetOrderConfigs: OrderConfig; currentOrderConfigs: OrderConfig; presetRowsAndColumns: DodAxisDef[] } {
        // get the saved layout config based on savedLayoutId selected by user
        const savedLayout = getSavedLayoutById(Number(savedLayoutId));

        const { rows, columns } = (savedLayout?.values as {
            layout: DodLayoutConfig;
            conditions: DodConditions;
        }).layout;
        const rowsAndColumns = [...rows, ...columns];

        // get the values and its order stored for each dimension (products, markets, facts, time_periods)
        // from the selected preset's layout config and put it inside a json map
        const presetOrderConfigs: OrderConfig = rowsAndColumns.reduce(
            (mapping, rc) => ({
                ...mapping,
                [rc.type]: rc.order,
            }),
            {}
        ) as OrderConfig;
        // we know products ordering needs special care, which is happening below
        const productsOrderInPreset = rowsAndColumns.filter((rc) => rc.type === 'products').map((rc) => rc.dim);
        presetOrderConfigs['products'] = productsOrderInPreset;

        // similarily, get the values and its order stored at the moment from context, based on user's selection
        // and create a map for each dimension (products, markets, time_periods and facts)
        const currentOrderConfigs: OrderConfig = rowColConfigs.reduce(
            (mapping, rc) => ({
                ...mapping,
                [rc.type]: rc.values,
            }),
            {}
        ) as OrderConfig;
        // again special care for products order below
        // (if you're not sure why this special care, its because of how rowColConfig is structured)
        const productsOrderInContext = rowColConfigs
            .filter((rcc) => rcc.type === 'products')
            .map((rcc) => (rcc.dim === '' ? 'categories' : rcc.dim)); //only dim is '' first time in default runconfig value
        currentOrderConfigs['products'] = productsOrderInContext;

        return {
            presetRowsAndColumns: rowsAndColumns,
            presetOrderConfigs,
            currentOrderConfigs,
        };
    }

    function getSortedMarkets(sortType: SortType): string[] {
        const { values, summedSelections } = runConfig.filters.markets;
        let sortedMarkets: string[] = [];
        if (['asc', 'desc'].includes(sortType)) {
            sortedMarkets = values
                .map((market) => market.name)
                .sort(sortType === 'asc' ? caseInsensitiveSort : caseInsensitiveReverseSort);
        }
        return [...sortedMarkets, ...summedSelections.map((ss) => ss.name)];
    }

    function getSortedTimePeriods(sortType: SortType): string[] {
        const { values, summedSelections } = runConfig.filters.timePeriods;
        let sortedTimePeriods: string[] = [];
        if (['asc', 'desc'].includes(sortType)) {
            sortedTimePeriods = values
                .flat()
                .map((tp) => new TimePeriodRange(tp, maxDate))
                .sort(sortType === 'asc' ? TimePeriodRange.compareAsc : TimePeriodRange.compareDesc)
                .map((tp) => tp.toLegacyDodString());
        }
        return [...sortedTimePeriods, ...summedSelections.map((ss) => ss.name)];
    }

    async function handlePresetLayoutChange(e: ByzzerChangeEvent<string>): Promise<void> {
        if (e.value === 'cl') {
            // custom layout doesn't impact the layout in anyway
            setRunConfig((config) => {
                handleUndoStates({ layout: config.layout });
                return {
                    ...config,
                    layout: {
                        ...value.layout,
                        quickLayoutCode: 'cl',
                        templateId: undefined,
                        savedLayoutId: undefined,
                    },
                };
            });
        } else {
            const isQuickLayout = e.value.startsWith('ql');

            if (
                isQuickLayout &&
                timePeriodsReordered &&
                !(await confirm({
                    title: 'Warning',
                    content: (
                        <>
                            <p>Applying a quick layout will revert your time periods back to their default order.</p>
                            <p>Do you want to continue?</p>
                        </>
                    ),
                    yesLabel: 'Yes',
                    noLabel: 'No, Cancel',
                    suppressable: true,
                    suppressMessage: 'Do not show this message again for the remainder of this run session.',
                    suppressed: suppressQuickLayoutWarning,
                    onSuppress() {
                        setSuppressQuickLayoutWarning(true);
                    },
                }))
            ) {
                return;
            }

            const quickLayoutCode = isQuickLayout ? e.value : undefined;
            const isSavedLayout = Object.keys(e.data.data).includes('conditions');
            const savedLayoutId = isQuickLayout ? undefined : isSavedLayout ? Number(e.value) : undefined;
            const templateId = isQuickLayout || isSavedLayout ? undefined : Number(e.value);

            let includeCategoryTotals = value.layout.includeCategoryTotals;
            let includeSubTotals = value.layout.includeSubTotals;
            let includeExtractDate = value.layout.includeExtractDate;
            let includeNoHistoryLabels = value.layout.includeNoHistoryLabels;
            let includeGranularityLevel = value.layout.includeGranularityLevel;
            let filterNotApplicable = value.layout.filterNotApplicable;

            let conditions = value.conditions;

            let newRowColConfigs: RowColConfig[] = [];

            if (isSavedLayout) {
                if (mode !== 'layout') {
                    const savedConditions = e.data.data?.conditions;
                    const savedConditionsObject =
                        savedConditions && !Array.isArray(savedConditions) ? savedConditions : undefined;
                    const savedFacts = Array.isArray(savedConditions)
                        ? savedConditions
                        : (savedConditionsObject?.facts ?? []);
                    const formattedFactNames = value.facts.map((fact) => factSetToDisplayNames(fact)).flat(Infinity);
                    const baseConditions = Array.isArray(value.conditions) ? {} : value.conditions;
                    const fallbackMarket =
                        Array.isArray(value.conditions) ? 'each_market' : (value.conditions?.market ?? 'each_market');
                    const fallbackTimePeriod =
                        Array.isArray(value.conditions)
                            ? 'each_time_period'
                            : (value.conditions?.timePeriod ?? 'each_time_period');

                    conditions = {
                        ...baseConditions,
                        ...(savedConditionsObject ?? {}),
                        facts: savedFacts.filter((condition) =>
                            formattedFactNames.some((fact) => condition.fact === fact)
                        ),
                        market: savedConditionsObject?.market ?? fallbackMarket,
                        timePeriod: savedConditionsObject?.timePeriod ?? fallbackTimePeriod,
                    };

                    includeCategoryTotals = e.data.data.layout.includeCategoryTotals;
                    includeSubTotals = e.data.data.layout.includeSubTotals;
                    includeExtractDate = e.data.data.layout.includeExtractDate;
                    includeNoHistoryLabels = e.data.data.layout.includeNoHistoryLabels ?? false;
                    includeGranularityLevel = e.data.data.layout.includeGranularityLevel ?? false;
                    filterNotApplicable = e.data.data.layout.filterNotApplicable ?? false;
                }

                const {
                    presetOrderConfigs,
                    currentOrderConfigs,
                    presetRowsAndColumns,
                } = getPresetLayoutOrderAndCurrentLayoutOrder(Number(e.value));

                // find what values are common between preset's order and current selection order and put it in a json map
                const intersectionOrderConfig = {
                    products: intersection(presetOrderConfigs['products'], currentOrderConfigs['products']),
                    markets: intersection(presetOrderConfigs['markets'], currentOrderConfigs['markets']),
                    time_periods: intersection(presetOrderConfigs['time_periods'], currentOrderConfigs['time_periods']),
                    facts: intersection(presetOrderConfigs['facts'], currentOrderConfigs['facts']),
                };

                // find what values present in current selection, but not in presets
                // we want this because thats what the user has selected to run this dod
                // we dont care of the values present in preset and not in current selection,
                // simply because user did not select them for this run
                const differenceOrderConfig = {
                    products: difference(currentOrderConfigs['products'], presetOrderConfigs['products']),
                    markets: difference(currentOrderConfigs['markets'], presetOrderConfigs['markets']),
                    time_periods: difference(currentOrderConfigs['time_periods'], presetOrderConfigs['time_periods']),
                    facts: difference(currentOrderConfigs['facts'], presetOrderConfigs['facts']),
                };

                const marketSort = presetRowsAndColumns.find((rcc) => rcc.type === 'markets')?.sortType;
                const timePeriodSort = presetRowsAndColumns.find((rcc) => rcc.type === 'time_periods')?.sortType;

                // as per requirement discussed for ordering in BYZ-8480, when a saved layout is applied in layout step,
                // we place the common values first and then append the remaining values from current selection,
                // which is happening below for all dimensions
                const resultOrderConfig = {
                    products: [...intersectionOrderConfig['products'], ...differenceOrderConfig['products']],
                    markets:
                        marketSort === 'asc' || marketSort === 'desc'
                            ? getSortedMarkets(marketSort)
                            : [...intersectionOrderConfig['markets'], ...differenceOrderConfig['markets']],
                    time_periods:
                        timePeriodSort === 'asc' || timePeriodSort === 'desc'
                            ? getSortedTimePeriods(timePeriodSort)
                            : [...intersectionOrderConfig['time_periods'], ...differenceOrderConfig['time_periods']],
                    facts: [...intersectionOrderConfig['facts'], ...differenceOrderConfig['facts']],
                };

                const presetRowColConfigs = applyPresetLayout(e.data.data.layout);

                // for markets, timeperiods and facts, its pretty straight-forward, we just need to update the 'values',
                // other config like axis, stack, page, type, dim all remains the same
                const nonProducts = presetRowColConfigs
                    .filter((config) => config.type !== 'products')
                    .map((config) => ({
                        ...config,
                        values: resultOrderConfig[config.type],
                    }));

                // however for products, we search if the dim already exists in previous rowColConfig,
                // if present, just return that object and not modify anything, as we want to preserve
                // its config like page, stack, hide which user might have selected
                const products: RowColConfig[] = resultOrderConfig['products'].map((dim) => {
                    if (presetRowColConfigs.find((config) => config.dim === dim)) {
                        if (dim === 'categories' && hasPartiallyApprovedMarkets) {
                            return {
                                ...(presetRowColConfigs.find((config) => config.dim === dim) as RowColConfig),
                                hide: false,
                                stack: false,
                            };
                        }
                        return presetRowColConfigs.find((config) => config.dim === dim) as RowColConfig;
                    } else {
                        // if the dim isn't found in previous value of rowColConfig, then its time to create one object for it
                        return {
                            // we want to preserve the axis as any other product dim, rest is all default values
                            axis: presetRowColConfigs.filter((config) => config.type === 'products')[0].axis,
                            type: 'products',
                            dim: dim,
                            values: [],
                            sortType: 'default',
                            stack: false,
                            hide: false,
                            pageBy: false,
                        } as RowColConfig;
                    }
                });

                // finally, combine both product's rowColConfig and non-product's rowColConfig and return the value per overallUiOrder
                const combinedRowColConfig = [...nonProducts, ...products];
                const overallOrderForPreset = uniq(presetRowColConfigs.map((prcc) => prcc.type));
                newRowColConfigs = overallOrderForPreset // to get the overall order to be applied from selected saved preset
                    .map((type) => combinedRowColConfig.filter((rcc) => rcc.type === type))
                    .flat();

                // we need this to throw a warning popup to user
                const excludedPresetSelections = {
                    products: difference(presetOrderConfigs['products'], currentOrderConfigs['products']),
                    markets: difference(presetOrderConfigs['markets'], currentOrderConfigs['markets']),
                    time_periods: difference(presetOrderConfigs['time_periods'], currentOrderConfigs['time_periods']),
                    facts: difference(presetOrderConfigs['facts'], currentOrderConfigs['facts']),
                };

                const excludedProducts = excludedPresetSelections['products'].map((dim) =>
                    presetRowsAndColumns.find((rcc) => rcc.dim === dim)
                );
                const pageBySelected = excludedProducts.filter((product) => (product as DodRowDef)?.pageBy);
                const stackBySelected = excludedProducts.filter((product) => (product as DodRowDef)?.stack);

                let alertContent = '';
                if (pageBySelected.length) {
                    alertContent = `This layout includes Page By for ${pageBySelected
                        .map((product) =>
                            SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                ? SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                : product?.dim
                        )
                        .join(', ')}. You have not included ${pageBySelected
                        .map((product) =>
                            SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                ? SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                : product?.dim
                        )
                        .join(', ')} in this run, so it will not be included in the Page By.`;
                }

                if (stackBySelected.length) {
                    alertContent = `This layout includes Stacking for ${stackBySelected
                        .map((product) =>
                            SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                ? SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                : product?.dim
                        )
                        .join(', ')}. You have not included ${stackBySelected
                        .map((product) =>
                            SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                ? SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                : product?.dim
                        )
                        .join(', ')} in the run, so it will not be Stacked.`;
                }

                if (pageBySelected.length && stackBySelected.length) {
                    alertContent = `This layout includes Page By for ${pageBySelected
                        .map((product) =>
                            SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                ? SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                : product?.dim
                        )
                        .join(', ')} and Stacking for ${stackBySelected
                        .map((product) =>
                            SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                ? SUB_DIMENSION_VALUE_TO_DISPLAY[product?.dim!]
                                : product?.dim
                        )
                        .join(
                            ', '
                        )}. You have not included them in this run, so it will not be included in the Page By and will not be Stacked.`;
                }
                if (pageBySelected.length || stackBySelected.length) {
                    byzzerAlert({
                        content: alertContent,
                    });
                }
            } else {
                newRowColConfigs = applyPresetLayout(e.data.data);
            }

            // Explicitly selecting any quick layout resets time-period defaults; reopening a saved run does not invoke this action.
            if (isQuickLayout) {
                const { values, summedSelections } = runConfig.filters.timePeriods;
                const orderedTimePeriods: string[] = values
                    .flat()
                    .map((tp) => new TimePeriodRange(tp, maxDate))
                    .sort(TimePeriodRange.compareDesc)
                    .map((tp) => tp.toLegacyDodString());

                // update the order in the row/cols
                newRowColConfigs.forEach((config) => {
                    if (config.type === 'time_periods') {
                        config.values = [...orderedTimePeriods, ...summedSelections.map((ss) => ss.name)];
                    }
                });
            }
            const layout: DodLayoutConfig = {
                ...runConfig.layout,
                quickLayoutCode,
                templateId,
                savedLayoutId,
                includeCategoryTotals,
                includeSubTotals,
                includeExtractDate,
                includeNoHistoryLabels,
                includeGranularityLevel,
                filterNotApplicable,
                overallUiOrder: uniq(newRowColConfigs.map((config) => config.type)),
                rows: newRowColConfigs.filter((v) => v.axis === 'row').map(toAxisDef) as DodRowDef[],
                columns: newRowColConfigs.filter((v) => v.axis === 'col').map(toAxisDef) as DodColDef[],
            };
            // set the base config based on the quick layout
            applyValues((prevValue) => {
                handleUndoStates({
                    timePeriodsReordered: prevValue.timePeriodsReordered,
                    rowColConfig: prevValue.rowColConfigs,
                    layout: prevValue.runConfig.layout,
                    conditions: prevValue.runConfig.conditions,
                });
                return {
                    ...prevValue,
                    timePeriodsReordered: false,
                    rowColConfigs: newRowColConfigs,
                    runConfig: {
                        ...prevValue.runConfig,
                        layout,
                        conditions,
                    },
                };
            });
        }

        // layout change tracking
        trackEvent({
            type: 'click',
            name: 'dod_preset_layout_change',
            data: getEventData({ dodWizardStep: 'layout', layout: isNaN(Number(e.value)) ? e.value : 'template' }),
        });
    }

    function applyPresetLayout(presetConfig: DodPresetConfig): RowColConfig[] {
        const { axes, order, dimConfigs } = cloneDeep(presetConfig);

        // Insert market_characteristics right after markets in the order
        const orderedTypes = [...order];
        const marketIndex = orderedTypes.indexOf('markets');
        if (marketIndex !== -1) {
            orderedTypes.splice(marketIndex + 1, 0, 'market_characteristics');
        }
        const allTypes = new Set(orderedTypes);

        const result = Array.from(allTypes)
            .map((type) => {
                const axis = axes[type];
                // Only process types that have an axis defined in the preset
                if (!axis) return [];

                return rowColConfigs
                    .filter((rcc) => rcc.type === type)
                    .map((rcc) => {
                        const dimConfig: Partial<RowColConfig> = {
                            stack: false,
                            hide: false,
                            pageBy: false,
                            sortType: 'default',
                            ...dimConfigs[rcc.dim === '' ? 'categories' : rcc.dim], // dim is only '' when default layout value is selected, need to handle like QL6's config (page by category)
                        };

                        return {
                            ...rcc,
                            dim: rcc.dim === '' ? 'categories' : rcc.dim, // dim is only '' when default layout value is selected, need to handle like QL6's config (page by category)
                            axis,
                            ...dimConfig,
                        };
                    });
            })
            .flat();
        
        return result;
    }

    function handleConditionsClear(): void {
        setRunConfig((config) => {
            handleUndoStates({ conditions: config.conditions });
            return {
                ...config,
                layout: {
                    ...config.layout,
                },
                conditions: DEFAULT_RUN_CONFIG.conditions,
            };
        });
    }

    async function handleConditionsEdit(): Promise<void> {
        const result = await openDodConditionBuilderModal({
            runConfig: value,
            maxDate,
        });

        if (result) {
            setRunConfig((config) => {
                handleUndoStates({ conditions: config.conditions });
                return {
                    ...config,
                    layout: {
                        ...config.layout,
                        // Adding non-default layout conditions can trigger layout-tab API flows, so we
                        // promote the config to step 5 only when the returned condition set is meaningful.
                        ...(hasSelectedConditions(result)
                            ? { extractWizardStep: 5 as const, wizardState: 5 as const }
                            : {}),
                    },
                    conditions: result,
                };
            });
        }

        // conditions button click tracking
        trackEvent({
            type: 'click',
            name: 'dod_conditions_click',
            data: getEventData({ dodWizardStep: 'layout' }),
        });
    }

    function handleUndoStates(config: UndoState): void {
        const undoState: UndoState = {
            ...(config?.timePeriodsReordered !== undefined && { timePeriodsReordered: config.timePeriodsReordered }),
            ...(config?.conditions && { conditions: config.conditions }),
            ...(config?.layout && { layout: config.layout }),
            ...(config?.rowColConfig && { rowColConfig: config?.rowColConfig }),
        };
        addStateToHistory(undoState);
    }

    function handleUndo(): void {
        if (!canUndo) return;

        const undoState = undo();
        if (!undoState) return;

        const { rowColConfig, layout, timePeriodsReordered, conditions } = undoState;

        const updates: Partial<DodRunConfig> = {};
        if (layout) updates.layout = layout;
        if (conditions) updates.conditions = conditions;

        if (rowColConfig) {
            setRowColConfigs(rowColConfig);
            applyValues((prevValue) => ({
                ...prevValue,
                rowColConfigs: rowColConfig,
                ...(timePeriodsReordered !== undefined && { timePeriodsReordered }),
            }));
        } else if (timePeriodsReordered !== undefined) {
            setTimePeriodsReordered(timePeriodsReordered);
        }

        setRunConfig((prevConfig) => ({
            ...prevConfig,
            ...updates,
            layout: {
                ...prevConfig.layout,
                ...(updates.layout ?? {}),
            },
        }));

        trackEvent({
            type: 'click',
            name: 'dod_layout_undo_click',
            data: getEventData({ dodWizardStep: 'layout' }),
        });
    }

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
                handleUndo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleUndo]);

    const handleSaveSelections = async () => {
        setSaveSelectionModalOpened(true);
        trackEvent({
            type: 'click',
            name: 'dod_save_selections_click',
            data: getEventData({ dodWizardStep: savedSelectionType, panel: 'layout' }),
        });

        const preset = await openCreateDodPresetModal({
            title: 'Save Your Layout',
            description: savedSelectionDescription,
            preset: {
                type: savedSelectionType,
                values: {
                    layout: { ...value.layout, overallUiOrder: uniq(rowColConfigs.map((config) => config.type)) }, // need to override overallUiOrder property in context while saving layout
                    conditions: value.conditions,
                },
            },
            // currentPreset: presets[savedSelectionType],
            dodPresetsMap: dodPresetsMap['layout'],
            duplicateNameErrorTip:
                'Your company already has a layout saved with this name. Please choose a different name.',
        });

        if (preset) {
            setPreset(savedSelectionType, {
                ...preset,
                // @ts-ignore - this needs to be here to remove values and produce a valid preset summary
                values: undefined,
            });
            reloadDodPresets();
            setRunConfig((config) => {
                handleUndoStates({
                    layout: {
                        ...config.layout,
                    },
                });
                return {
                    ...config,
                    layout: {
                        ...value.layout,
                        templateId: undefined,
                        quickLayoutCode: undefined,
                        savedLayoutId: preset.id,
                    },
                };
            });
        }
        setSaveSelectionModalOpened(false);
    };

    function hasAccessToDelete(savedLayoutId: number): boolean {
        let hasAccess = false;

        const loggedInUserId = user?.authorizedUserId;
        const savedLayout = dodPresetsMap.layout.find(
            (layoutPreset) => Number(layoutPreset.id) === Number(savedLayoutId)
        );

        if (savedLayout && savedLayout.authorizedUserId === loggedInUserId) {
            hasAccess = true;
        }
        return hasAccess;
    }

    const isActivelyHovered = (itemVal) => {
        return hoveredItem === itemVal.toString();
    };

    const handleSetDefaultLayout = async (e, hoveredLayoutId) => {
        setSavingDefaultLayout(true);
        try {
            defaultSelection = hoveredLayoutId;
            await saveDefaultDodLayout({ defaultDodLayoutId: hoveredLayoutId });
            await refreshAccount();
            setSavingDefaultLayout(false);
        } catch {
            setSavingDefaultLayout(false);
        }
    };

    const handleDeselectDefaultLayout = async (e, hoveredLayoutId) => {
        setSavingDefaultLayout(true);
        try {
            await deleteDefaultDodLayout();
            await refreshAccount();
            setSavingDefaultLayout(false);
        } catch {
            setSavingDefaultLayout(false);
        }
    };

    // custom component to pass to react-select which controls the way selected value is displayed in select component
    const SingleValueComponent = (props) => {
        return (
            <span className={`${baseClassName}__select-value`}>
                <span>{props.children}</span>
                {isDefaultLayout(props.data.value) && (
                    <Tippy content={'This is your default layout'} placement={'top'} delay={[500, 0]}>
                        <i className={`${baseClassName}__select-value--filled-pin`} />
                    </Tippy>
                )}
            </span>
        );
    };

    // custom component which is listed as each option in select component
    const CustomLayoutOption = (props) => {
        return (
            <span className={`${baseClassName}__value-node`} onMouseEnter={() => handleMouseEnter(props.data.value)}>
                <span className={`${baseClassName}__value-node--label`}>{props.children}</span>
                <span className={`${baseClassName}__value-node--content`}>
                    {isDefaultLayout(props.data.value) && (
                        <Tippy content={'Remove from default'} placement={'top'} delay={[500, 0]}>
                            <i
                                className={`${baseClassName}__value-node--filled-pin`}
                                onClick={async (e) => await handleDeselectDefaultLayout(e, props.data.value)}
                            />
                        </Tippy>
                    )}
                    {isActivelyHovered(props.data.value) && !isDefaultLayout(props.data.value) && (
                        <Tippy content={'Save as default'} placement={'top'} delay={[500, 0]}>
                            <i
                                className={`${baseClassName}__value-node--empty-pin`}
                                onClick={async (e) => await handleSetDefaultLayout(e, props.data.value)}
                            />
                        </Tippy>
                    )}
                    {hasAccessToDelete(props.value) && (
                        <i
                            className={`${baseClassName}__value-node--delete`}
                            onClick={() => handleSavedSelectionDelete(Number(props.data.value))}
                        />
                    )}
                </span>
            </span>
        );
    };

    async function handleSavedSelectionDelete(id: number): Promise<void> {
        openModal({
            title: 'Delete Saved Layout',
            showCloseOption: false,
            content: ({ busy }) => {
                return (
                    <>
                        {busy ? (
                            <p>Removing Saved Layout...</p>
                        ) : (
                            <>
                                <p>Are you sure you want to delete this Saved Layout?</p>
                                {isDefaultLayout(id) && <p>This will also delete your default layout.</p>}
                            </>
                        )}
                    </>
                );
            },
            actions: [
                {
                    key: 'cancel',
                    label: 'No, Cancel',
                    type: 'negative',
                    action({ reject }) {
                        reject();
                    },
                    disableIf: ({ busy }) => (busy ? true : false),
                },
                {
                    key: 'yes',
                    label: 'Yes',
                    async action({ resolve, setBusy }) {
                        try {
                            setBusy(true);
                            await deletePreset(id);
                            if (isDefaultLayout(id)) {
                                await deleteDefaultDodLayout();
                                await refreshAccount();
                            }
                            // need to reset the select component, rowColConfigs and layout to QL1 configuration post successful deletion
                            applyValues((prevValue) => {
                                const updatedRcc = prevValue.rowColConfigs.map((rcc) => ({
                                    ...rcc,
                                    hide: false,
                                    pageBy: false,
                                    stack: false,
                                    sort: 'default',
                                    sortType: 'default',
                                    axis: rcc.type === 'products' ? 'row' : 'col',
                                }));

                                const consolidatedRowsCols = [
                                    ...prevValue.runConfig.layout.rows,
                                    ...prevValue.runConfig.layout.columns,
                                ];

                                return {
                                    ...prevValue,
                                    runConfig: {
                                        ...prevValue.runConfig,
                                        conditions: DEFAULT_RUN_CONFIG.conditions,
                                        layout: {
                                            ...DEFAULT_RUN_CONFIG.layout,
                                            rows: consolidatedRowsCols.filter((rc) => rc.type === 'products'),
                                            columns: consolidatedRowsCols.filter((rc) => rc.type !== 'products'),
                                        },
                                    },
                                    rowColConfigs: [
                                        // rowColConfigs responsible for preserving the overallUiOrder, hence this dedicated destructuring
                                        updatedRcc.filter((rcc) => rcc.type === 'products'),
                                        updatedRcc.filter((rcc) => rcc.type === 'markets'),
                                        updatedRcc.filter((rcc) => rcc.type === 'time_periods'),
                                        updatedRcc.filter((rcc) => rcc.type === 'facts'),
                                    ].flat(),
                                };
                            });
                            resolve(true);
                        } finally {
                            setBusy(false);
                        }
                    },
                    disableIf: ({ busy }) => (busy ? true : false),
                },
            ],
        });
    }

    return (
        <DodPanel
            className={classnames(baseClassName, className)}
            title={title}
            expandable={true}
            name={'dod-layout-editor'}
            enableFilter={false}
            actions={[
                {
                    key: 'undo',
                    disabled: !canUndo,
                    iconType: 'undo',
                    tip: 'Undo your selections',
                    onClick: handleUndo,
                },
                {
                    key: 'save',
                    disabled: false,
                    iconType: 'save',
                    tip: 'Save your current layout selections to use in future runs.',
                    onClick: handleSaveSelections,
                },
            ]}
            forceClosePanel={saveSelectionModalOpened}
        >
            <ByzzerMask loading={savingDefaultLayout} />{' '}
            {/** loader required to maintain proper state of app and avoid redundant data and ui behaviour when saving default layout */}
            <ByzzerSelect
                value={layoutId}
                className={`${baseClassName}__preset-picker`}
                options={layoutOptions}
                label={'Preset & Saved Layouts:'}
                placeholder="Select Your Layout"
                onChange={handlePresetLayoutChange}
                disabledOptions={disabledQuickLayoutOptions}
                optionComponent={CustomLayoutOption}
                singleValueComponent={SingleValueComponent}
            />
            <DodDesignYourLayout runConfig={runConfig} value={value.layout} handleUndoStates={handleUndoStates} />
            <DodLayoutOptions value={runConfig.layout} onChange={handleLayoutChange} mode={mode} />
            <DodLayoutConditionBuilder
                value={runConfig.conditions.facts}
                facts={runConfig.facts}
                name={runConfig.conditions.name}
                onClear={handleConditionsClear}
                onEdit={handleConditionsEdit}
                mode={mode}
                conditionMarket={runConfig.conditions.market}
                conditionMarketDisplay={resolveConditionMarketDisplay(runConfig)}
                conditionTimePeriod={runConfig.conditions.timePeriod}
                conditionTimePeriodDisplay={resolveConditionTimePeriodDisplay(runConfig, maxDate)}
            />
        </DodPanel>
    );
}

function resolveConditionMarketDisplay(runConfig: DodRunConfig): string | undefined {
    const marketKey = runConfig.conditions?.market;
    if (!marketKey || marketKey === 'each_market') return undefined;
    const market = runConfig.filters?.markets?.values?.find(m => m.key === marketKey);
    if (market) return market.name;
    const summed = runConfig.filters?.markets?.summedSelections?.find(s => s.name === marketKey);
    if (summed) return summed.name;
    return marketKey;
}

function resolveConditionTimePeriodDisplay(runConfig: DodRunConfig, maxDate: string): string | undefined {
    const tp = runConfig.conditions?.timePeriod;
    if (!tp || tp === 'each_time_period') return undefined;
    try {
        return new TimePeriodRange(tp, maxDate).toLegacyDodString();
    } catch {
        // check if it's a summed time period name
        const summed = runConfig.filters?.timePeriods?.summedSelections?.find(s => s.name === tp);
        if (summed) return summed.name;
        return tp;
    }
}

function toDodPresetConfig(layout: DodLayoutConfig): DodPresetConfig {
    const { rows, columns, overallUiOrder } = layout;

    const rowsAndColumns = [...rows, ...columns];

    // convert the array of rows and columsn into their respective configs
    const dimConfigs = rowsAndColumns.reduce(
        (mapping, rc) => ({
            ...mapping,
            [rc.dim]: {
                stack: (rc as DodRowDef).stack,
                pageBy: rc.pageBy,
                hide: rc.hide,
                sortType: rc.sortType,
            },
        }),
        {}
    );

    // determine which axis each dimenison type is on. it has to be row or col so if it's not row, you guessed it, it's col
    const products = rows.find((row) => row.type === 'products') ? 'row' : 'col';
    const markets = rows.find((row) => row.type === 'markets') ? 'row' : 'col';
    const market_characteristics = rows.find((row) => row.type === 'market_characteristics') ? 'row' : 'col';
    const time_periods = rows.find((row) => row.type === 'time_periods') ? 'row' : 'col';
    const facts = rows.find((row) => row.type === 'facts') ? 'row' : 'col';

    return {
        axes: {
            products,
            markets,
            market_characteristics,
            time_periods,
            facts,
},
        dimConfigs,
        order: overallUiOrder,
    };
}

export default DodLayoutEditor;
